/**
 * Valid roles in the system
 */
export type ValidRole = "STUDENT" | "TEACHER" | "ORG_ADMIN" | "mod" | "admin";

export const VALID_ROLES: readonly ValidRole[] = [
  "STUDENT",
  "TEACHER",
  "ORG_ADMIN",
  "mod",
  "admin",
] as const;

/**
 * Whether a roles string grants a routable app view (STUDENT or TEACHER).
 *
 * Used to detect a stale cached role after the et-auth account_role split:
 * a pre-split cache may hold account_type values ("user"/"demo"/"admin")
 * that no longer route, so callers should defer to a fresh session sync.
 *
 * @param userRoles - The user's role(s), can be comma-separated
 */
export function isRoutableRole(userRoles: string | undefined | null): boolean {
  return hasRole(userRoles, "STUDENT") || hasRole(userRoles, "TEACHER");
}

/**
 * Check if a user's roles string contains a specific role.
 * Roles can be a single role or comma-separated (e.g., "STUDENT,admin")
 *
 * @param userRoles - The user's role(s), can be comma-separated
 * @param targetRole - The role to check for
 * @returns true if the user has the target role
 */
export function hasRole(
  userRoles: string | undefined | null,
  targetRole: ValidRole,
): boolean {
  if (!userRoles) return false;
  const roles = userRoles.split(",").map((r) => r.trim());
  if (roles.includes(targetRole)) return true;
  // Org admins can access any teacher-gated view (any teacher's library).
  if (targetRole === "TEACHER" && roles.includes("ORG_ADMIN")) return true;
  return false;
}

/**
 * Check if a user has any of the specified roles
 *
 * @param userRoles - The user's role(s), can be comma-separated
 * @param targetRoles - Array of roles to check for
 * @returns true if the user has any of the target roles
 */
export function hasAnyRole(
  userRoles: string | undefined | null,
  targetRoles: ValidRole[],
): boolean {
  if (!userRoles) return false;
  const roles = userRoles.split(",").map((r) => r.trim());
  return targetRoles.some((target) => roles.includes(target));
}

/**
 * Get all roles from a comma-separated roles string
 *
 * @param userRoles - The user's role(s), can be comma-separated
 * @returns Array of individual roles
 */
export function getRoles(userRoles: string | undefined | null): string[] {
  if (!userRoles) return [];
  return userRoles.split(",").map((r) => r.trim());
}

/**
 * Check if user is a teacher (has TEACHER role)
 */
export function isTeacher(userRoles: string | undefined | null): boolean {
  return hasRole(userRoles, "TEACHER");
}

/**
 * Check if user is a student (has STUDENT role)
 */
export function isStudent(userRoles: string | undefined | null): boolean {
  return hasRole(userRoles, "STUDENT");
}

/**
 * Check if user is an admin
 */
export function isAdmin(userRoles: string | undefined | null): boolean {
  return hasRole(userRoles, "admin");
}

/**
 * Check if user is a moderator
 */
export function isMod(userRoles: string | undefined | null): boolean {
  return hasRole(userRoles, "mod");
}

/**
 * True when the user is an ORG_ADMIN who hasn't selected a teacher to act as.
 * Used to gate Spaces access: org admins may only enter when act-as is active.
 */
export function isOrgAdminWithoutActingTeacher(
  userRoles: string | undefined | null,
  actingTeacherId: number | null | undefined,
): boolean {
  return (
    !hasAnyRole(userRoles, ["TEACHER"]) &&
    hasAnyRole(userRoles, ["ORG_ADMIN"]) &&
    !actingTeacherId
  );
}

/**
 * Get the primary role for redirect purposes.
 * Priority: TEACHER > STUDENT (for redirect logic)
 */
export function getPrimaryRole(
  userRoles: string | undefined | null,
): "TEACHER" | "STUDENT" {
  if (isTeacher(userRoles)) return "TEACHER";
  return "STUDENT";
}
